import { useEffect, useState } from 'react'
import { useAdminStore } from '../../store/adminStore'
import Spinner from '../../components/common/Spinner'
import type { CrowdSubmission } from '../../types'

function formatPrice(sub: CrowdSubmission): string | null {
  if (sub.price_usd != null) return `$${Number(sub.price_usd).toLocaleString()}`
  if (sub.price_uzs != null) return `${Number(sub.price_uzs).toLocaleString()} UZS`
  return null
}

function workerLabel(sub: CrowdSubmission): string {
  if (sub.worker_first_name && sub.worker_username) return `${sub.worker_first_name} (@${sub.worker_username})`
  if (sub.worker_first_name) return sub.worker_first_name
  if (sub.worker_username) return `@${sub.worker_username}`
  return `Worker ${String(sub.worker_id).slice(0, 8)}`
}

// ── Lightbox ──────────────────────────────────────────────────────────────────
function Lightbox({ url, onClose }: { url: string; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.9)' }}
      onClick={onClose}
    >
      <button
        className="absolute top-4 right-4 text-white text-3xl font-bold opacity-70 hover:opacity-100"
        style={{ background: 'none', border: 'none' }}
        onClick={onClose}
      >
        ×
      </button>
      <img
        src={url}
        className="max-w-full max-h-full rounded-xl"
        style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain' }}
        onClick={(e) => e.stopPropagation()}
        alt=""
      />
    </div>
  )
}

// ── Reject modal ──────────────────────────────────────────────────────────────
function RejectModal({
  onConfirm,
  onCancel,
}: {
  onConfirm: (comment: string) => void
  onCancel: () => void
}) {
  const [comment, setComment] = useState('')
  return (
    <div
      className="fixed inset-0 z-[90] flex items-end justify-center pb-6 px-4"
      style={{ background: 'rgba(0,0,0,0.6)' }}
      onClick={onCancel}
    >
      <div
        className="w-full max-w-lg rounded-2xl p-4 space-y-3"
        style={{ background: 'var(--app-card-bg)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <p className="font-semibold text-sm" style={{ color: 'var(--tg-theme-text-color)' }}>
          Reject reason (optional)
        </p>
        <textarea
          className="w-full rounded-xl p-3 text-sm resize-none"
          style={{
            background: 'var(--app-page-bg)',
            color: 'var(--tg-theme-text-color)',
            border: '1.5px solid transparent',
            outline: 'none',
            minHeight: 72,
          }}
          placeholder="Explain why this submission is rejected…"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          autoFocus
        />
        <div className="flex gap-2">
          <button
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold"
            style={{ background: 'var(--app-page-bg)', color: 'var(--tg-theme-text-color)' }}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold"
            style={{ background: '#ef4444', color: 'white' }}
            onClick={() => onConfirm(comment)}
          >
            Reject
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Submission card ───────────────────────────────────────────────────────────
function SubmissionCard({
  sub,
  onVerify,
  onVerifyAndPublish,
  onReject,
}: {
  sub: CrowdSubmission
  onVerify: () => Promise<void>
  onVerifyAndPublish: () => Promise<void>
  onReject: (comment?: string) => Promise<void>
}) {
  const [busy, setBusy] = useState(false)
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)
  const [showRejectModal, setShowRejectModal] = useState(false)

  const handle = async (fn: () => Promise<void>) => {
    setBusy(true)
    try {
      await fn()
    } finally {
      setBusy(false)
    }
  }

  const isFound = sub.submission_type === 'found'
  const price = formatPrice(sub)

  return (
    <>
      {lightboxUrl && <Lightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />}
      {showRejectModal && (
        <RejectModal
          onCancel={() => setShowRejectModal(false)}
          onConfirm={(comment) => {
            setShowRejectModal(false)
            handle(() => onReject(comment || undefined))
          }}
        />
      )}

      <div
        className="rounded-2xl overflow-hidden"
        style={{ background: 'var(--app-card-bg)', border: '1px solid rgba(128,128,128,0.1)' }}
      >
        {/* ── Header strip ── */}
        <div
          className="flex items-center gap-2 px-3 py-2"
          style={{ background: isFound ? '#22c55e18' : '#ef444418' }}
        >
          <span
            className="text-xs px-2 py-0.5 rounded-full font-semibold"
            style={{
              background: isFound ? '#22c55e' : '#ef4444',
              color: 'white',
            }}
          >
            {isFound ? '✓ Found' : '✗ Not Found'}
          </span>

          {/* Worker */}
          <span className="text-xs font-medium flex items-center gap-1" style={{ color: 'var(--tg-theme-hint-color)' }}>
            👤 {workerLabel(sub)}
          </span>

          <span className="text-xs opacity-40 ml-auto" style={{ color: 'var(--tg-theme-text-color)' }}>
            {new Date(sub.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>

        <div className="p-3 space-y-3">
          {/* ── Task / item context ── */}
          {(sub.task_title || sub.task_item_name) && (
            <div
              className="rounded-xl px-2.5 py-1.5 text-xs space-y-0.5"
              style={{ background: 'rgba(99,102,241,0.1)' }}
            >
              {sub.task_title && (
                <p className="font-semibold" style={{ color: '#818cf8' }}>
                  📋 {sub.task_title}
                </p>
              )}
              {sub.task_item_name && (
                <p style={{ color: '#a5b4fc' }}>
                  Item: {sub.task_item_name}
                </p>
              )}
            </div>
          )}

          {/* ── Photos ── */}
          {sub.photos && sub.photos.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {sub.photos.map((p) => {
                const src = p.medium_url || `/api/v1/media/${p.file_path}`
                const thumb = p.thumbnail_url || src
                return (
                  <button
                    key={p.id}
                    className="relative flex-shrink-0 rounded-xl overflow-hidden"
                    style={{ background: 'none', border: 'none', padding: 0 }}
                    onClick={() => setLightboxUrl(src)}
                  >
                    <img
                      src={thumb}
                      className="w-20 h-20 object-cover rounded-xl"
                      alt=""
                    />
                    <div
                      className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 rounded-xl"
                      style={{ background: 'rgba(0,0,0,0.35)', transition: 'opacity 0.15s' }}
                    >
                      <span className="text-white text-lg">🔍</span>
                    </div>
                  </button>
                )
              })}
            </div>
          )}

          {/* ── Product + price ── */}
          <div>
            {sub.product_name && (
              <p className="font-semibold text-sm" style={{ color: 'var(--tg-theme-text-color)' }}>
                {sub.product_name}
                {sub.product_brand && (
                  <span className="opacity-50 font-normal"> · {sub.product_brand}</span>
                )}
              </p>
            )}
            {price && (
              <p className="text-base font-bold mt-0.5" style={{ color: '#22c55e' }}>
                {price}
              </p>
            )}
          </div>

          {/* ── Shop info ── */}
          {(sub.shop_name || sub.stall_section) && (
            <div
              className="rounded-xl px-2.5 py-2 text-xs space-y-0.5"
              style={{ background: 'var(--app-page-bg)' }}
            >
              {sub.shop_name && (
                <p className="font-medium" style={{ color: 'var(--tg-theme-text-color)' }}>
                  🏪 {sub.shop_name}
                </p>
              )}
              {sub.stall_section && (
                <p style={{ color: 'var(--tg-theme-hint-color)' }}>Section: {sub.stall_section}</p>
              )}
              {sub.shop_address && (
                <p style={{ color: 'var(--tg-theme-hint-color)' }}>📍 {sub.shop_address}</p>
              )}
              {sub.shop_latitude && sub.shop_longitude && (
                <p style={{ color: 'var(--tg-theme-hint-color)' }}>
                  GPS: {Number(sub.shop_latitude).toFixed(5)}, {Number(sub.shop_longitude).toFixed(5)}
                </p>
              )}
            </div>
          )}

          {/* ── Comment ── */}
          {sub.comment && (
            <p
              className="text-xs italic"
              style={{ color: 'var(--tg-theme-hint-color)', borderLeft: '2px solid var(--tg-theme-hint-color)', paddingLeft: 8 }}
            >
              "{sub.comment}"
            </p>
          )}

          {/* ── Potential coins badge ── */}
          {isFound && sub.potential_coins != null && (
            <div className="flex items-center gap-1.5">
              <span
                className="text-xs px-2.5 py-1 rounded-full font-semibold"
                style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24' }}
              >
                🪙 +{sub.potential_coins} coins if verified
              </span>
            </div>
          )}

          {/* ── Actions ── */}
          <div className="flex gap-2 pt-1">
            <button
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-40"
              style={{ background: '#22c55e', color: 'white' }}
              disabled={busy}
              onClick={() => handle(onVerify)}
            >
              {busy ? '…' : 'Verify'}
            </button>
            {sub.created_product_id && (
              <button
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-40"
                style={{ background: '#16a34a', color: 'white' }}
                disabled={busy}
                onClick={() => handle(onVerifyAndPublish)}
              >
                {busy ? '…' : 'Verify & Publish'}
              </button>
            )}
            <button
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-40"
              style={{ background: '#ef444418', color: '#ef4444', border: '1px solid #ef444440' }}
              disabled={busy}
              onClick={() => setShowRejectModal(true)}
            >
              Reject
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function AdminSubmissionsPage() {
  const { pendingSubmissions, isLoading, error, fetchPendingSubmissions, verifySubmission } =
    useAdminStore()

  useEffect(() => {
    fetchPendingSubmissions()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleAction = async (
    id: string,
    status: 'verified' | 'rejected',
    auto_publish = false,
    comment?: string,
  ) => {
    try {
      await verifySubmission(id, status, comment, auto_publish)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed')
    }
  }

  if (isLoading) return <Spinner />

  return (
    <div className="p-4 space-y-3 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between pb-1">
        <p className="text-sm font-semibold" style={{ color: 'var(--tg-theme-text-color)' }}>
          {pendingSubmissions.length > 0
            ? `${pendingSubmissions.length} pending review`
            : 'No pending submissions'}
        </p>
        <button
          className="text-xs px-3 py-1.5 rounded-xl font-medium"
          style={{ background: 'var(--app-card-bg)', color: 'var(--tg-theme-hint-color)' }}
          onClick={() => fetchPendingSubmissions()}
        >
          Refresh
        </button>
      </div>

      {error && (
        <p className="text-sm text-center py-4" style={{ color: '#ef4444' }}>
          {error}
        </p>
      )}

      {!isLoading && pendingSubmissions.length === 0 && !error && (
        <div className="flex flex-col items-center py-16 gap-3">
          <span className="text-4xl">✅</span>
          <p className="text-sm opacity-40" style={{ color: 'var(--tg-theme-text-color)' }}>
            All caught up — no pending submissions
          </p>
        </div>
      )}

      {pendingSubmissions.map((sub) => (
        <SubmissionCard
          key={sub.id}
          sub={sub}
          onVerify={() => handleAction(sub.id, 'verified')}
          onVerifyAndPublish={() => handleAction(sub.id, 'verified', true)}
          onReject={(comment) => handleAction(sub.id, 'rejected', false, comment)}
        />
      ))}
    </div>
  )
}
